import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

// Animation variants from Header.js
const aiPopupContainerVariants = {
    hidden: { opacity: 0, transition: { when: "afterChildren", staggerChildren: 0.1, staggerDirection: -1 } },
    visible: { opacity: 1, transition: { when: "beforeChildren", staggerChildren: 0.2 } }
};
const aiPopupContentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 12 } }
};
const shinySparkleVariants = {
    animate: {
        opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5], rotate: [0, 180, 360],
        transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
    }
};

function MobileAiPopupPage({ onAskAiClick }) {
    const navigate = useNavigate();

    // Prevent background scrolling
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, 8000);

        return () => clearTimeout(timer);
    }, []);
    
    const handleClose = () => {
        navigate(-1); // Go back to the previous page
    };

    const handleQuickOrder = () => {
        onAskAiClick(); // Open the AI chat dialog
        handleClose(); // Close this page
    };

    return (
        <motion.div
            variants={aiPopupContainerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 1500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(circle, #85bb5c, #75a84b)',
                padding: '24px',
                boxSizing: 'border-box'
            }}
        >
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute', top: 24, right: 24, color: 'white',
                    bgcolor: 'rgba(0,0,0,0.1)', '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ textAlign: 'center', color: 'white' }}>
                <motion.div variants={aiPopupContentVariants}>
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: '4rem', color: '#f0f5e4' }} />
                        <motion.div
                            variants={shinySparkleVariants}
                            animate="animate"
                            style={{ position: 'absolute', top: '0px', right: '-10px', fontSize: '1.5rem' }}
                        >
                            ✨
                        </motion.div>
                    </Box>
                </motion.div>

                <Typography
                    variant="h3" component="h2"
                    sx={{ fontWeight: 'bold', mb: 2, lineHeight: 1.2, textShadow: '1px 1px 4px rgba(0,0,0,0.4)' }}
                >
                    {"Submit a quick order with AI".split(" ").map((word, index) => (
                        <motion.span
                            key={index}
                            variants={aiPopupContentVariants}
                            style={{ display: 'inline-block', marginRight: '0.5rem' }}
                        >
                            {word}
                        </motion.span>
                    ))}
                </Typography>

                <motion.div variants={aiPopupContentVariants}>
                    <Typography
                        variant="h6"
                        sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}
                    >
                        Just type what you need. It's fast, easy, and smart.
                    </Typography>
                </motion.div>

                <motion.div
                    variants={aiPopupContentVariants}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleQuickOrder}
                        sx={{
                            bgcolor: 'white',
                            color: '#5a8f3c',
                            borderRadius: '50px',
                            py: 1.5,
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            maxWidth: '300px',
                            boxShadow: '0px 4px 15px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                bgcolor: '#f8f9fa',
                                boxShadow: '0px 6px 20px rgba(0,0,0,0.15)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Quick Order Now
                    </Button>
                </motion.div>
            </Box>
        </motion.div>
    );
}

export default MobileAiPopupPage;